import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    IconButton,
} from "@mui/material";
import { Delete, Edit, Add } from "@mui/icons-material";
import { useAtom } from "jotai";
import { InsertChartOutlined } from "@mui/icons-material";


import { AccountType } from "../../types/enums/userEnums";
import {
    AccountRootCreate,
    AccountRootResponse,
    AccountRootUpdate,
} from "../../types/models";
import {
    createAccountRoot,
    deleteAccountRoot,
    updateAccountRoot,
} from "../../services/AccountRoot/service";
import { accountRootsAtom, fetchAccountRootsAtom } from "../../state/atoms/accountRootsAtom";
import ConfirmDialog from "../../components/ConfirmDialog";
import ReportDialog from "../../components/ReportDialog";

const AccountRootsPage: React.FC = () => {
    const [accountRoots, setAccountRoots] = useAtom(accountRootsAtom);
    const [, fetchAccountRoots] = useAtom(fetchAccountRootsAtom);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRoot, setSelectedRoot] = useState<AccountRootResponse | null>(null);

    const [formData, setFormData] = useState<AccountRootCreate | AccountRootUpdate>({
        name: "",
        email: "",
        phone: "",
        account_type: AccountType.CLIENT,
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rootToDelete, setRootToDelete] = useState<number | null>(null);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [reportAccountRootId, setReportAccountRootId] = useState<number | null>(null);
    const [reportAccountRootName, setReportAccountRootName] = useState<string>("")


    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                await fetchAccountRoots();
            } catch (err) {
                console.error("Error fetching account roots:", err);
                setError((err as Error).message);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [fetchAccountRoots]);

    const handleOpenCreate = () => {
        setIsEdit(false);
        setFormData({
            name: "",
            email: "",
            phone: "",
            account_type: AccountType.CLIENT,
        });
        setOpenDialog(true);
    };

    const handleOpenEdit = (root: AccountRootResponse) => {
        setIsEdit(true);
        setSelectedRoot(root);
        setFormData({
            name: root.name,
            email: root.email,
            phone: root.phone,
            account_type: root.account_type,
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRoot(null);
    };

    const handleFormChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        try {
            if (isEdit && selectedRoot) {
                await updateAccountRoot(selectedRoot.id, formData as AccountRootUpdate);
            } else {
                await createAccountRoot(formData as AccountRootCreate);
            }
            await fetchAccountRoots();
            handleCloseDialog();
        } catch (err) {
            console.error("Error saving account root:", err);
            alert((err as Error).message);
        }
    };

    const handleDeleteClick = (rootId: number) => {
        setRootToDelete(rootId);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (rootToDelete !== null) {
            try {
                await deleteAccountRoot(rootToDelete);
                await fetchAccountRoots();
            } catch (err) {
                console.error("Error deleting account root:", err);
                alert((err as Error).message);
            } finally {
                setDeleteDialogOpen(false);
                setRootToDelete(null);
            }
        }
    };
    const handleOpenReportDialog = (rootId: number, rootName: string) => {
        setReportAccountRootId(rootId);
        setReportAccountRootName(rootName);
        setReportDialogOpen(true);
    };


    return (
        <Box sx={{ color: "#fff" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
                <Typography variant="h4">Account Roots</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenCreate}
                    sx={{ backgroundColor: "#2ea043", color: "#fff" }}
                >
                    New Root
                </Button>
            </Box>

            {loading ? (
                <CircularProgress sx={{ color: "#fff" }} />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{ backgroundColor: "transparent" }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: "#A8B2D1" }}>Name</TableCell>
                                <TableCell sx={{ color: "#A8B2D1" }}>Email</TableCell>
                                <TableCell sx={{ color: "#A8B2D1" }}>Phone</TableCell>
                                <TableCell sx={{ color: "#A8B2D1" }}>Account Type</TableCell>
                                <TableCell sx={{ color: "#A8B2D1" }}>Created At</TableCell>
                                <TableCell sx={{ color: "#A8B2D1" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {accountRoots.map((root) => (
                                <TableRow key={root.id} sx={{ backgroundColor: "transparent" }}>
                                    <TableCell sx={{ color: "#fff", backgroundColor: "transparent" }}>{root.name}</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>{root.email}</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>{root.phone || "—"}</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>{root.account_type}</TableCell>
                                    <TableCell sx={{ color: "#fff" }}>
                                        {new Date(root.created_at).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleOpenEdit(root)} color="primary">
                                            <Edit />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteClick(root.id)} color="error">
                                            <Delete />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleOpenReportDialog(root.id, root.name)}
                                            color="info"
                                        >
                                            <InsertChartOutlined />
                                        </IconButton>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ backgroundColor: "#161B22", color: "#fff" }}>
                    {isEdit ? "Edit Account Root" : "Create Account Root"}
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: "#161B22" }}>
                    <TextField
                        label="Name"
                        name="name"
                        fullWidth
                        margin="normal"
                        value={formData.name}
                        onChange={handleFormChange}
                        sx={{ input: { color: "#fff" }, label: { color: "#A8B2D1" } }}
                    />
                    <TextField
                        label="Email"
                        name="email"
                        fullWidth
                        margin="normal"
                        value={formData.email}
                        onChange={handleFormChange}
                        sx={{ input: { color: "#fff" }, label: { color: "#A8B2D1" } }}
                    />
                    <TextField
                        label="Phone"
                        name="phone"
                        fullWidth
                        margin="normal"
                        value={formData.phone || ""}
                        onChange={handleFormChange}
                        sx={{ input: { color: "#fff" }, label: { color: "#A8B2D1" } }}
                    />
                    <TextField
                        label="Account Type"
                        name="account_type"
                        fullWidth
                        margin="normal"
                        select
                        value={formData.account_type}
                        onChange={handleFormChange}
                        sx={{ input: { color: "#fff" }, label: { color: "#A8B2D1" } }}
                    >
                        {Object.values(AccountType).map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: "#161B22", pb: 2 }}>
                    <Button onClick={handleCloseDialog} sx={{ color: "#A8B2D1" }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "#2ea043" }}>
                        {isEdit ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={deleteDialogOpen}
                title="Delete Account Root"
                message="Are you sure you want to delete this account root?"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteDialogOpen(false)}
                confirmText="Delete"
                cancelText="Cancel"
            />
            {reportAccountRootId !== null && (
                <ReportDialog
                    open={reportDialogOpen}
                    onClose={() => setReportDialogOpen(false)}
                    accountRootId={reportAccountRootId}
                    accountRootName={reportAccountRootName}
                />
            )}

        </Box>
    );
};

export default AccountRootsPage;
